import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import type { GridRenderCellParams } from '@mui/x-data-grid';
import { Paper, Popper, Typography } from 'src/components/atoms';

interface Props {
  params: GridRenderCellParams;
}

function isOverflown(element: Element) {
  return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
}

const StyledTableCell = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;
const StyledTableCellValue = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledDefaultCellText = styled.div`
  position: absolute;
  display: block;
  height: 1l;
  top: 0;
`;

export const PopperCell = ({ params }: Props) => {
  const wrapper = useRef<HTMLDivElement | null>(null);
  const cellDiv = useRef(null);
  const cellValue = useRef(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showFullCell, setShowFullCell] = useState(false);
  const [showPopper, setShowPopper] = useState(false);

  useEffect(() => {
    if (!showFullCell) {
      return undefined;
    }

    function handleKeyDown(nativeEvent: KeyboardEvent) {
      if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
        setShowFullCell(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showFullCell]);

  const handleMouseEnter = () => {
    const isCurrentlyOverflown = isOverflown(cellValue.current!);
    setShowPopper(isCurrentlyOverflown);
    setAnchorEl(cellDiv.current);
    setShowFullCell(true);
  };

  const handleMouseLeave = () => {
    setShowFullCell(false);
  };

  return (
    <StyledTableCell ref={wrapper} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <StyledDefaultCellText ref={cellDiv} />
      <StyledTableCellValue ref={cellValue}>{params.value}</StyledTableCellValue>
      {showPopper && (
        <Popper
          open={showFullCell && anchorEl != null}
          anchorEl={anchorEl}
          placement='bottom-end'
          style={{ width: params.colDef.width! - 10 }}
        >
          <Paper elevation={1} style={{ minHeight: wrapper.current!.offsetHeight - 3 }}>
            <Typography variant='body2' style={{ padding: 8 }}>
              {params.value}
            </Typography>
          </Paper>
        </Popper>
      )}
    </StyledTableCell>
  );
};
